import { useMemo } from "react";
import { useGetAllScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { useGetAllScore } from "src/api/sure-api/score/useGetAllScore";

export type IngredientScoreField = {
    scoreId: number;
    label: string;
    code: string | null;
    unit: string | null;
};

/**
 * Combines the platform-level `ScoreType[type=ingredient]` configuration with
 * the sure-api Score catalog to produce the list of score fields that should
 * appear in the platform IngredientReference form.
 *
 * Returns the deduplicated union of `scoreIds` and `subscoreIds` from
 * `ScoreType[ingredient]`, hydrated with label/unit/code from sure-api.
 */
export const useIngredientScoreFields = () => {
    const { scoreTypes, isLoading: isLoadingTypes } = useGetAllScoreType();
    const { scores: sureScores, isLoading: isLoadingScores } = useGetAllScore();

    const fields = useMemo<IngredientScoreField[]>(() => {
        if (!scoreTypes || !sureScores) return [];

        const ingredientType = scoreTypes.find((t) => t.type === "ingredient");
        if (!ingredientType) return [];

        const ids = new Set<number>([...(ingredientType.scoreIds ?? []), ...(ingredientType.subscoreIds ?? [])]);

        const scoreById = new Map<number, (typeof sureScores)[number]>();
        for (const score of sureScores) {
            scoreById.set(score.id, score);
        }

        const result: IngredientScoreField[] = [];
        for (const id of ids) {
            const meta = scoreById.get(id);
            // Prefer `title` (score name in the catalog) over `label` (often a short
            // per-variation phrasing e.g. "per kg of product" from translations).
            const displayName = meta?.title || meta?.label || meta?.code || `#${id}`;
            result.push({
                scoreId: id,
                label: displayName,
                code: meta?.code ?? null,
                unit: meta?.unit?.abbreviation ?? null,
            });
        }

        return result.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [scoreTypes, sureScores]);

    return {
        fields,
        isLoading: isLoadingTypes || isLoadingScores,
    };
};
